import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { api } from '@/lib/api';
import { EarningsResponse, CommissionStatement } from '@/types';
import { Layout } from '@/components/layout/Layout';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { StatementStatusBadge, formatPeriodLabel, describeEventBasis } from '@/components/StatementStatusBadge';
import { formatCurrency, formatDate, formatDateOnly, cn } from '@/lib/utils';
import { Loader2, Wallet } from 'lucide-react';

export function EarningsPage() {
  const [data, setData] = useState<EarningsResponse | null>(null);
  const [selected, setSelected] = useState<CommissionStatement | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [isLoadingStatement, setIsLoadingStatement] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    api
      .getEarnings()
      .then((response) => {
        setData(response);
        if (response.statements.length > 0) {
          void openStatement(response.statements[0].id);
        }
      })
      .catch((err: { error?: string }) => setError(err.error || 'Could not load your earnings'))
      .finally(() => setIsLoading(false));
  }, []);

  const openStatement = async (id: string) => {
    setIsLoadingStatement(true);
    try {
      const { statement } = await api.getEarningsStatement(id);
      setSelected(statement);
    } catch (err) {
      console.error('Failed to load statement:', err);
    } finally {
      setIsLoadingStatement(false);
    }
  };

  const year = new Date().getFullYear();

  return (
    <Layout>
      <div className="space-y-6">
        <div>
          <h1 className="text-3xl font-bold text-gray-900">Earnings</h1>
          <p className="text-gray-600 mt-1">Commission statements for the clients you referred</p>
        </div>

        {isLoading ? (
          <div className="flex items-center justify-center py-12">
            <Loader2 className="h-8 w-8 animate-spin text-abby-primary" />
          </div>
        ) : error || !data ? (
          <Card>
            <CardContent className="py-8 text-center text-gray-600">{error || 'Earnings unavailable'}</CardContent>
          </Card>
        ) : (
          <>
            {!data.terms || data.terms.model === 'none' ? (
              <Card>
                <CardHeader>
                  <CardTitle>No commission on your program</CardTitle>
                  <CardDescription>
                    Your referrals still receive the offer shown in the{' '}
                    <Link to="/toolkit" className="text-abby-primary hover:underline">
                      Referral Toolkit
                    </Link>
                    . Contact Partner Support if you think this is wrong.
                    {data.statements.length > 0 ? ' Your earlier statements remain below.' : ''}
                  </CardDescription>
                </CardHeader>
              </Card>
            ) : (
              <>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <Tile tone="yellow" label="Pending review" value={data.summary.pending + data.summary.open_month_total} hint="includes the current period and any carried-over amounts" />
              <Tile
                tone="blue"
                label="Approved, awaiting payout"
                value={data.summary.approved}
                hint={`paid by check, ${data.terms!.payout_frequency}${data.terms!.minimum_payout > 0 ? `, once over ${formatCurrency(data.terms!.minimum_payout)}` : ''}`}
              />
              <Tile tone="green" label={`Paid in ${year}`} value={data.summary.paid_year_to_date} hint={`${formatCurrency(data.summary.paid_total)} paid all time`} />
            </div>

            <Card>
              <CardHeader>
                <CardTitle>Your program</CardTitle>
                <CardDescription>{data.terms!.description}</CardDescription>
              </CardHeader>
              <CardContent className="text-sm text-gray-600">
                Statements are prepared {data.terms!.payout_frequency} and paid by check once Abby approves them. Terms effective
                from {formatDateOnly(data.terms!.effective_from)}.
              </CardContent>
            </Card>
              </>
            )}

            {(data.terms && data.terms.model !== 'none') || data.statements.length > 0 ? (
            <div className="grid grid-cols-1 lg:grid-cols-[minmax(0,1fr)_minmax(0,1.6fr)] gap-6">
              <Card>
                <CardHeader>
                  <CardTitle>Statements</CardTitle>
                </CardHeader>
                <CardContent>
                  {data.statements.length === 0 && data.open_periods.length === 0 ? (
                    <div className="flex flex-col items-center py-8 text-center">
                      <Wallet className="h-10 w-10 text-gray-400 mb-3" />
                      <p className="text-gray-600 text-sm">Nothing earned yet. Statements appear once a referred client is billed.</p>
                    </div>
                  ) : (
                    <table className="w-full text-sm">
                      <thead>
                        <tr className="border-b text-left text-gray-500">
                          <th className="pb-2 font-medium">Period</th>
                          <th className="pb-2 font-medium text-right">Amount</th>
                          <th className="pb-2 font-medium">Status</th>
                        </tr>
                      </thead>
                      <tbody className="divide-y">
                        {data.open_periods.map((p) => (
                          <tr key={p.period} className="text-gray-700">
                            <td className="py-2 whitespace-nowrap">{formatPeriodLabel(p.period)}</td>
                            <td className="py-2 text-right tabular-nums">{formatCurrency(p.total)}</td>
                            <td className="py-2 text-xs text-gray-500">{p.carried_over ? 'Carried over (under minimum)' : 'In progress'}</td>
                          </tr>
                        ))}
                        {data.statements.map((s) => (
                          <tr
                            key={s.id}
                            onClick={() => openStatement(s.id)}
                            className={cn('cursor-pointer hover:bg-gray-50', selected?.id === s.id && 'bg-gray-50')}
                          >
                            <td className="py-2 font-medium text-gray-900 whitespace-nowrap">{formatPeriodLabel(s.period_label)}</td>
                            <td className="py-2 text-right tabular-nums">{formatCurrency(s.total_amount)}</td>
                            <td className="py-2">
                              <StatementStatusBadge status={s.status} />
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  )}
                </CardContent>
              </Card>

              <Card>
                <CardHeader>
                  <CardTitle>{selected ? `${formatPeriodLabel(selected.period_label)} statement` : 'Statement detail'}</CardTitle>
                  <CardDescription>
                    {selected
                      ? `${selected.line_count} line${selected.line_count === 1 ? '' : 's'}${
                          selected.status === 'paid' && selected.paid_at
                            ? ` · paid ${formatDate(selected.paid_at)}${selected.paid_reference ? `, ref ${selected.paid_reference}` : ''}`
                            : ''
                        }`
                      : 'Select a statement to see its lines'}
                  </CardDescription>
                </CardHeader>
                <CardContent>
                  {isLoadingStatement ? (
                    <div className="flex justify-center py-8">
                      <Loader2 className="h-6 w-6 animate-spin text-abby-primary" />
                    </div>
                  ) : selected?.events ? (
                    <div className="overflow-x-auto">
                      <table className="w-full text-sm">
                        <thead>
                          <tr className="border-b text-left text-gray-500">
                            <th className="pb-2 font-medium">Client</th>
                            <th className="pb-2 font-medium">Month</th>
                            <th className="pb-2 font-medium">Basis</th>
                            <th className="pb-2 font-medium text-right">Amount</th>
                          </tr>
                        </thead>
                        <tbody className="divide-y">
                          {selected.events.map((e) => (
                            <tr key={e.id}>
                              <td className="py-2">
                                <Link to={`/referrals/${e.sf_account_id}`} className="text-abby-primary hover:underline">
                                  {e.account_name}
                                </Link>
                              </td>
                              <td className="py-2 text-gray-600 whitespace-nowrap">{formatPeriodLabel(e.period)}</td>
                              <td className="py-2 text-gray-600">{describeEventBasis(e, formatCurrency)}</td>
                              <td className="py-2 text-right tabular-nums font-medium">{formatCurrency(e.amount)}</td>
                            </tr>
                          ))}
                          <tr>
                            <td colSpan={3} className="py-2 font-semibold">
                              Total
                            </td>
                            <td className="py-2 text-right tabular-nums font-semibold">{formatCurrency(selected.total_amount)}</td>
                          </tr>
                        </tbody>
                      </table>
                    </div>
                  ) : (
                    <p className="text-sm text-gray-500">No statement selected.</p>
                  )}
                </CardContent>
              </Card>
            </div>
            ) : null}
          </>
        )}
      </div>
    </Layout>
  );
}

function Tile({ tone, label, value, hint }: { tone: 'yellow' | 'blue' | 'green'; label: string; value: number; hint: string }) {
  const tones = {
    yellow: 'bg-yellow-50 border-yellow-200 text-yellow-800',
    blue: 'bg-blue-50 border-blue-200 text-blue-800',
    green: 'bg-green-50 border-green-200 text-green-800',
  };
  return (
    <div className={cn('rounded-lg border p-4', tones[tone])}>
      <p className="text-sm font-medium mb-1">{label}</p>
      <p className="text-2xl font-bold tabular-nums">{formatCurrency(value)}</p>
      <p className="text-xs opacity-80 mt-1">{hint}</p>
    </div>
  );
}
